import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheckCircle,
  faArrowRight,
  faHome,
  faReceipt,
  faStar,
  faMagic,
  faGift,
  faTimesCircle,
  faSpinner,
  faExclamationTriangle,
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Header from '../Shared/Header';
import { decryptData } from '../../utils/encryption';

const PaymentResult = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('checking');
  const [showConfetti, setShowConfetti] = useState(false);
  const [failureReason, setFailureReason] = useState('');

  useEffect(() => {
    // Paymob sends data in the URL query strings
    // e.g. ?success=true&amount_cents=1000&order=123...
    const success = decryptData(searchParams.get('success'));
    // Extract failure reason from various possible parameter names
    const errorMessage = decryptData(searchParams.get('error'));

    if (success === true) {
      setStatus('success');
      setShowConfetti(true);
      // Hide confetti after 5 seconds
      setTimeout(() => setShowConfetti(false), 5000);
      // Optional: Call your API here to double-check status if needed
    } else {
      setStatus('failed');
      setFailureReason(errorMessage);
    }
  }, [searchParams]);

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 },
    },
  };

  const iconVariants = {
    hidden: { scale: 0, rotate: -180 },
    visible: {
      scale: 1,
      rotate: 0,
      transition: {
        type: 'spring',
        stiffness: 200,
        damping: 15,
      },
    },
  };

  const pulseVariants = {
    pulse: {
      scale: [1, 1.05, 1],
      transition: {
        duration: 2,
        repeat: Infinity,
        ease: 'easeInOut',
      },
    },
  };

  const spinVariants = {
    spin: {
      rotate: 360,
      transition: {
        duration: 1,
        repeat: Infinity,
        ease: 'linear',
      },
    },
  };

  // Get status-specific content
  const getStatusContent = () => {
    switch (status) {
      case 'success':
        return {
          icon: faCheckCircle,
          iconColor: '#10b981',
          bgGradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          badge: { text: 'تم بنجاح', color: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' },
          title: 'شكراً لك!',
          subtitle: 'تمت عملية الدفع بنجاح',
          description: 'نشكرك على ثقتك بنا. تم تفعيل اشتراكك وأصبح بإمكانك الآن الاستفادة من جميع المزايا والخدمات المتاحة.',
          showFeatures: true,
          showActions: true,
        };
      case 'pending':
        return {
          icon: faSpinner,
          iconColor: '#6366f1',
          bgGradient: 'linear-gradient(135deg, #6366f1 0%, #d97706 100%)',
          badge: { text: 'قيد المعالجة', color: 'linear-gradient(135deg, #6366f1 0%, #d97706 100%)' },
          title: 'جاري المعالجة',
          subtitle: 'عملية الدفع قيد المراجعة',
          description: 'عملية الدفع الخاصة بك قيد المعالجة. سيتم تفعيل اشتراكك فور تأكيد الدفع. سنرسل لك إشعاراً عبر البريد الإلكتروني.',
          showFeatures: false,
          showActions: true,
        };
      case 'failed':
        return {
          icon: faTimesCircle,
          iconColor: '#ef4444',
          bgGradient: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
          badge: { text: 'فشلت العملية', color: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' },
          title: 'عذراً!',
          subtitle: 'فشلت عملية الدفع',
          description: 'لم تتم عملية الدفع بنجاح. يرجى التحقق من بياناتك والمحاولة مرة أخرى. إذا استمرت المشكلة، يرجى التواصل مع الدعم الفني.',
          showFeatures: false,
          showActions: true,
        };
      default:
        return {
          icon: faSpinner,
          iconColor: '#6366f1',
          bgGradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          badge: { text: 'جاري التحقق', color: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)' },
          title: 'جاري التحقق...',
          subtitle: 'يرجى الانتظار',
          description: 'جاري التحقق من حالة الدفع...',
          showFeatures: false,
          showActions: false,
        };
    }
  };

  const content = getStatusContent();

  return (
    <>
      <div className="payment-result-page">
        {/* Animated Background */}
        <div className="animated-bg">
          <div className="gradient-orb orb-1"></div>
          <div className="gradient-orb orb-2"></div>
          <div className="gradient-orb orb-3"></div>
        </div>

        {/* Confetti Effect - Only for success */}
        {showConfetti && status === 'success' && (
          <div className="confetti-container">
            {[...Array(50)].map((_, i) => (
              <div
                key={i}
                className="confetti"
                style={{
                  left: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 3}s`,
                  backgroundColor: ['#6366F1', '#8B5CF6', '#EC4899', '#6366f1', '#10B981'][
                    Math.floor(Math.random() * 5)
                  ],
                }}
              />
            ))}
          </div>
        )}

        <AnimatePresence mode="wait">
          <motion.div
            key={status}
            className="result-container"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            exit={{ opacity: 0, scale: 0.9 }}
          >
            {/* Status Icon */}
            <motion.div className="icon-wrapper" variants={iconVariants}>
              <motion.div
                className="status-icon"
                variants={status === 'checking' || status === 'pending' ? spinVariants : pulseVariants}
                animate={status === 'checking' || status === 'pending' ? 'spin' : 'pulse'}
                style={{ color: content.iconColor }}
              >
                <FontAwesomeIcon icon={content.icon} />
              </motion.div>
              <div className="icon-glow" style={{ background: `radial-gradient(circle, ${content.iconColor}40 0%, transparent 70%)` }}></div>
            </motion.div>

            {/* Status Message */}
            <motion.div className="content" variants={itemVariants}>
              <div className="badge" style={{ background: content.badge.color }}>
                <FontAwesomeIcon icon={faMagic} />
                <span>{content.badge.text}</span>
              </div>
              <h1 className="title">{content.title}</h1>
              <h2 className="subtitle">{content.subtitle}</h2>
              <p className="description">{content.description}</p>
            </motion.div>

            {/* Features Grid - Only for success */}
            {content.showFeatures && (
              <motion.div className="features-grid" variants={itemVariants}>
                <div className="feature-card">
                  <div className="feature-icon">
                    <FontAwesomeIcon icon={faGift} />
                  </div>
                  <h3>وصول فوري</h3>
                  <p>استمتع بجميع المحتويات الآن</p>
                </div>
                <div className="feature-card">
                  <div className="feature-icon">
                    <FontAwesomeIcon icon={faStar} />
                  </div>
                  <h3>محتوى حصري</h3>
                  <p>احصل على أفضل المواد التعليمية</p>
                </div>
                <div className="feature-card">
                  <div className="feature-icon">
                    <FontAwesomeIcon icon={faReceipt} />
                  </div>
                  <h3>فاتورة إلكترونية</h3>
                  <p>تم إرسالها إلى بريدك الإلكتروني</p>
                </div>
              </motion.div>
            )}

            {/* Action Buttons */}
            {content.showActions && (
              <motion.div className="actions" variants={itemVariants}>
                <motion.button
                  className="btn btn-primary"
                  onClick={() => navigate('/')}
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <FontAwesomeIcon icon={faHome} />
                  <span>العودة للرئيسية</span>
                </motion.button>
                {status === 'success' && (
                  <motion.button
                    className="btn btn-secondary"
                    onClick={() => navigate('/teacher/dashboard?tab=subscription')}
                    whileHover={{ scale: 1.05, y: -2 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <span>عرض الاشتراك</span>
                    <FontAwesomeIcon icon={faArrowRight} />
                  </motion.button>
                )}
                {status === 'failed' && (
                  <motion.button
                    className="btn btn-secondary"
                    onClick={() => navigate('/pricing')}
                    whileHover={{ scale: 1.05, y: -2 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <span>المحاولة مرة أخرى</span>
                    <FontAwesomeIcon icon={faArrowRight} />
                  </motion.button>
                )}
              </motion.div>
            )}

            {/* Additional Info */}
            {status === 'success' && (
              <motion.div className="info-box success-info" variants={itemVariants}>
                <p>
                  <FontAwesomeIcon icon={faMagic} className="info-icon" />
                  سيتم إرسال تفاصيل الاشتراك إلى بريدك الإلكتروني خلال دقائق
                </p>
              </motion.div>
            )}
            {status === 'pending' && (
              <motion.div className="info-box pending-info" variants={itemVariants}>
                <p>
                  <FontAwesomeIcon icon={faExclamationTriangle} className="info-icon" />
                  قد تستغرق عملية المعالجة بضع دقائق. سنبقيك على اطلاع
                </p>
              </motion.div>
            )}
            {/* {status === 'failed' && (
              <>
                {failureReason && (
                  <motion.div className="info-box failure-reason-box" variants={itemVariants}>
                    <p>
                      <FontAwesomeIcon icon={faExclamationTriangle} className="info-icon" />
                      <strong>سبب الفشل:</strong> {failureReason}
                    </p>
                  </motion.div>
                )}
                <motion.div className="info-box error-info" variants={itemVariants}>
                  <p>
                    <FontAwesomeIcon icon={faExclamationTriangle} className="info-icon" />
                    إذا تم خصم المبلغ، سيتم استرجاعه خلال 3-5 أيام عمل
                  </p>
                </motion.div>
              </>
            )} */}
          </motion.div>
        </AnimatePresence>

        <style jsx>{`
          .payment-result-page {
            min-height: 100vh;
            background: ${content.bgGradient};
            position: relative;
            overflow: hidden;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 100px 24px 60px;
            direction: rtl;
            font-family: 'Tajawal', sans-serif;
            transition: background 0.5s ease;
          }

          /* Animated Background */
          .animated-bg {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            overflow: hidden;
            z-index: 0;
          }

          .gradient-orb {
            position: absolute;
            border-radius: 50%;
            filter: blur(80px);
            opacity: 0.4;
            animation: float 20s ease-in-out infinite;
          }

          .orb-1 {
            width: 400px;
            height: 400px;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            top: -100px;
            right: -100px;
            animation-delay: 0s;
          }

          .orb-2 {
            width: 500px;
            height: 500px;
            background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
            bottom: -150px;
            left: -150px;
            animation-delay: 7s;
          }

          .orb-3 {
            width: 350px;
            height: 350px;
            background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            animation-delay: 14s;
          }

          @keyframes float {
            0%,
            100% {
              transform: translate(0, 0) scale(1);
            }
            33% {
              transform: translate(50px, -50px) scale(1.1);
            }
            66% {
              transform: translate(-50px, 50px) scale(0.9);
            }
          }

          /* Confetti */
          .confetti-container {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            pointer-events: none;
            z-index: 100;
          }

          .confetti {
            position: absolute;
            width: 10px;
            height: 10px;
            top: -10px;
            animation: confetti-fall 3s linear forwards;
          }

          @keyframes confetti-fall {
            to {
              transform: translateY(100vh) rotate(360deg);
              opacity: 0;
            }
          }

          /* Result Container */
          .result-container {
            position: relative;
            z-index: 10;
            max-width: 700px;
            width: 100%;
            background: rgba(255, 255, 255, 0.95);
            backdrop-filter: blur(20px);
            border-radius: 32px;
            padding: 60px 40px;
            box-shadow: 0 30px 60px rgba(0, 0, 0, 0.3);
            text-align: center;
          }

          /* Icon */
          .icon-wrapper {
            position: relative;
            display: inline-block;
            margin-bottom: 32px;
          }

          .status-icon {
            font-size: 120px;
            position: relative;
            z-index: 2;
          }

          .icon-glow {
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            width: 150px;
            height: 150px;
            border-radius: 50%;
            z-index: 1;
            animation: glow-pulse 2s ease-in-out infinite;
          }

          @keyframes glow-pulse {
            0%,
            100% {
              transform: translate(-50%, -50%) scale(1);
              opacity: 0.5;
            }
            50% {
              transform: translate(-50%, -50%) scale(1.2);
              opacity: 0.8;
            }
          }

          /* Content */
          .content {
            margin-bottom: 40px;
          }

          .badge {
            display: inline-flex;
            align-items: center;
            gap: 8px;
            color: white;
            padding: 8px 20px;
            border-radius: 999px;
            font-size: 0.9rem;
            font-weight: 700;
            margin-bottom: 24px;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
          }

          .title {
            font-size: 3.5rem;
            font-weight: 900;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;
            margin-bottom: 16px;
            line-height: 1.2;
          }

          .subtitle {
            font-size: 1.8rem;
            font-weight: 700;
            color: #1e293b;
            margin-bottom: 16px;
          }

          .description {
            font-size: 1.1rem;
            color: #64748b;
            line-height: 1.8;
            max-width: 500px;
            margin: 0 auto;
          }

          /* Features Grid */
          .features-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
            gap: 20px;
            margin-bottom: 40px;
          }

          .feature-card {
            background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
            padding: 24px 16px;
            border-radius: 20px;
            transition: all 0.3s ease;
            border: 2px solid transparent;
          }

          .feature-card:hover {
            transform: translateY(-5px);
            border-color: #667eea;
            box-shadow: 0 10px 30px rgba(102, 126, 234, 0.2);
          }

          .feature-icon {
            font-size: 2.5rem;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;
            margin-bottom: 12px;
          }

          .feature-card h3 {
            font-size: 1.1rem;
            font-weight: 700;
            color: #1e293b;
            margin-bottom: 8px;
          }

          .feature-card p {
            font-size: 0.9rem;
            color: #64748b;
            line-height: 1.5;
          }

          /* Action Buttons */
          .actions {
            display: flex;
            gap: 16px;
            justify-content: center;
            flex-wrap: wrap;
            margin-bottom: 32px;
          }

          .btn {
            display: flex;
            align-items: center;
            gap: 12px;
            padding: 16px 32px;
            border: none;
            border-radius: 16px;
            font-size: 1.1rem;
            font-weight: 700;
            cursor: pointer;
            transition: all 0.3s ease;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
          }

          .btn-primary {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
          }

          .btn-primary:hover {
            box-shadow: 0 8px 20px rgba(102, 126, 234, 0.4);
          }

          .btn-secondary {
            background: white;
            color: #667eea;
            border: 2px solid #667eea;
          }

          .btn-secondary:hover {
            background: #667eea;
            color: white;
          }

          /* Info Box */
          .info-box {
            padding: 16px 24px;
            border-radius: 16px;
            border-left: 4px solid;
          }

          .success-info {
            background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
            border-left-color: #6366f1;
          }

          .pending-info {
            background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
            border-left-color: #6366f1;
          }

          .error-info {
            background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
            border-left-color: #ef4444;
          }

          .failure-reason-box {
            background: linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%);
            border-left-color: #f97316;
            margin-bottom: 16px;
          }

          .info-box p {
            display: flex;
            align-items: center;
            gap: 12px;
            justify-content: center;
            font-size: 0.95rem;
            font-weight: 600;
            margin: 0;
          }

          .success-info p {
            color: #78350f;
          }

          .pending-info p {
            color: #78350f;
          }

          .error-info p {
            color: #7f1d1d;
          }

          .failure-reason-box p {
            color: #7c2d12;
          }

          .info-icon {
            font-size: 1.2rem;
          }

          .success-info .info-icon {
            color: #6366f1;
          }

          .pending-info .info-icon {
            color: #6366f1;
          }

          .error-info .info-icon {
            color: #ef4444;
          }

          .failure-reason-box .info-icon {
            color: #f97316;
          }

          /* Responsive */
          @media (max-width: 768px) {
            .payment-result-page {
              padding: 80px 16px 40px;
            }

            .result-container {
              padding: 40px 24px;
              border-radius: 24px;
            }

            .status-icon {
              font-size: 80px;
            }

            .title {
              font-size: 2.5rem;
            }

            .subtitle {
              font-size: 1.4rem;
            }

            .description {
              font-size: 1rem;
            }

            .features-grid {
              grid-template-columns: 1fr;
              gap: 16px;
            }

            .actions {
              flex-direction: column;
              width: 100%;
            }

            .btn {
              width: 100%;
              justify-content: center;
            }

            .orb-1,
            .orb-2,
            .orb-3 {
              width: 250px;
              height: 250px;
            }
          }
        `}</style>
      </div>
    </>
  );
};

export default PaymentResult;
